.media-default-skin .media-slider {
  --media-track-size: calc(var(--media-spacing) * 1);
  --media-track-highlighted-size: calc(var(--media-spacing) * 1.75);
  --media-track-border-radius: 99px;
  --media-track-transition-duration: 100ms;
  --media-thumb-size: calc(var(--media-spacing) * 3);
  --media-chapter-gap: calc(var(--media-spacing) * 1);
  --media-internal-chapter-inset-start: calc(var(--media-chapter-gap) / 2);
  --media-internal-chapter-inset-end: calc(var(--media-chapter-gap) / 2);

  position: relative;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: none;

  &[data-orientation="horizontal"] {
    width: var(--media-slider-width, 100%);
    min-width: calc(var(--media-spacing) * 20);
    height: var(--media-slider-height, calc(var(--media-spacing) * 8));
  }
  &[data-orientation="vertical"] {
    width: var(--media-slider-width, calc(var(--media-spacing) * 8));
    height: var(--media-slider-height, calc(var(--media-spacing) * 20));
  }

  /* Track */
  .media-slider__track {
    position: relative;
    overflow: hidden;
    user-select: none;
    background-color: oklch(from currentColor l c h / 0.2);
    border-radius: var(--media-track-border-radius);
    isolation: isolate;

    &[data-orientation="horizontal"] {
      width: 100%;
      height: var(--media-track-size);
    }
    &[data-orientation="vertical"] {
      width: var(--media-track-size);
      height: 100%;
    }
  }

  /* Shared track fills */
  .media-slider__buffer,
  .media-slider__fill {
    position: absolute;
    pointer-events: none;
    border-radius: inherit;

    &[data-orientation="horizontal"] {
      inset-block: 0;
      left: 0;
      width: 100%;
    }
    &[data-orientation="vertical"] {
      inset-inline: 0;
      bottom: 0;
      height: 100%;
    }

    @media (prefers-reduced-motion: no-preference) {
      transition: clip-path var(--media-track-transition-duration) ease-out;
    }
  }
  &[data-dragging] {
    .media-slider__fill,
    .media-slider__buffer {
      transition-duration: 0ms;
    }
  }

  /* Buffer */
  .media-slider__buffer {
    background-color: oklch(from currentColor l c h / 0.2);

    &[data-orientation="horizontal"] {
      clip-path: inset(0 calc(100% - var(--media-slider-buffer)) 0 0 round var(--media-track-border-radius));
    }
    &[data-orientation="vertical"] {
      clip-path: inset(calc(100% - var(--media-slider-buffer)) 0 0 0 round var(--media-track-border-radius));
    }
  }

  /* Fill */
  .media-slider__fill {
    background-color: var(--media-internal-accent-color);

    &[data-orientation="horizontal"] {
      clip-path: inset(0 calc(100% - var(--media-slider-fill)) 0 0 round var(--media-track-border-radius));
    }
    &[data-orientation="vertical"] {
      clip-path: inset(calc(100% - var(--media-slider-fill)) 0 0 0 round var(--media-track-border-radius));
    }
  }
  &[data-dragging] {
    .media-slider__fill[data-orientation="horizontal"] {
      clip-path: inset(0 calc(100% - var(--media-slider-pointer)) 0 0 round var(--media-track-border-radius));
    }
    .media-slider__fill[data-orientation="vertical"] {
      clip-path: inset(calc(100% - var(--media-slider-pointer)) 0 0 0 round var(--media-track-border-radius));
    }
  }

  /* Chapters */
  .media-slider__chapters {
    position: relative;
    display: flex;
    flex: 1;
    align-items: center;
    min-width: 0;
    min-height: 0;
    border-radius: inherit;

    &[data-orientation="horizontal"] {
      width: 100%;
      height: 100%;
    }
    &[data-orientation="vertical"] {
      flex-direction: column-reverse;
      width: 100%;
      height: 100%;
    }
  }

  .media-slider__chapter {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    min-height: 0;
    border-radius: inherit;

    &:first-child {
      --media-internal-chapter-inset-start: 0px;
    }
    &:last-child {
      --media-internal-chapter-inset-end: 0px;
    }

    .media-slider__chapter-track {
      border-radius: inherit;

      @media (prefers-reduced-motion: no-preference) {
        transition: 200ms ease-out;
        transition-property: height, width;
      }
    }

    &[data-orientation="horizontal"] {
      clip-path: inset(0 calc(100% - var(--media-slider-chapter-end)) 0 var(--media-slider-chapter-start));

      .media-slider__chapter-track {
        height: var(--media-track-size);
        clip-path: inset(
          0 calc(100% - var(--media-slider-chapter-end) + var(--media-internal-chapter-inset-end)) 0
            calc(var(--media-slider-chapter-start) + var(--media-internal-chapter-inset-start)) round
            var(--media-track-border-radius)
        );
      }

      &[data-highlighted] .media-slider__chapter-track {
        height: var(--media-track-highlighted-size);
      }
    }

    &[data-orientation="vertical"] {
      clip-path: inset(calc(100% - var(--media-slider-chapter-end)) 0 var(--media-slider-chapter-start) 0);

      .media-slider__chapter-track {
        width: var(--media-track-size);
        clip-path: inset(
          calc(100% - var(--media-slider-chapter-end) + var(--media-internal-chapter-inset-end)) 0
            calc(var(--media-slider-chapter-start) + var(--media-internal-chapter-inset-start)) 0 round
            var(--media-track-border-radius)
        );
      }

      &[data-highlighted] .media-slider__chapter-track {
        width: var(--media-track-highlighted-size);
      }
    }
  }

  /* Thumb */
  .media-slider__thumb {
    position: absolute;
    z-index: 10;
    width: var(--media-thumb-size);
    height: var(--media-thumb-size);
    user-select: none;
    outline: 4px solid transparent;
    outline-offset: -4px;
    background-color: currentColor;
    border-radius: calc(Infinity * 1px);
    box-shadow:
      0 0 0 1px var(--media-shadow-current-color, oklch(0 0 0 / 0.1)),
      0 1px 3px 0 oklch(0 0 0 / 0.35),
      0 1px 2px -1px oklch(0 0 0 / 0.35);
    opacity: 0;
    scale: 0.8;
    translate: -50% -50%;

    &[data-orientation="horizontal"] {
      top: 50%;
      left: var(--media-slider-fill);
    }
    &[data-orientation="vertical"] {
      top: calc(100% - var(--media-slider-fill));
      left: 50%;
    }

    &:focus-visible {
      outline-color: oklch(from currentColor l c h / 0.15);
      outline-offset: 0;
      opacity: 1;
    }

    &::after {
      position: absolute;
      inset: -4px;
      content: "";
      border-radius: inherit;
      box-shadow: 0 0 0 2px currentColor;
    }

    &:not(:focus-visible)::after {
      opacity: 0;
      scale: 0.5;
    }

    &.media-slider__thumb--persistent {
      opacity: 1;
      scale: 1;
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        outline-color: oklch(from currentColor l c h / 0.15);
        outline-offset: 0;
      }
    }

    @media (prefers-reduced-motion: no-preference) {
      transition-timing-function: ease-out;
      transition-duration: var(--media-track-transition-duration);
      transition-property: opacity, outline-offset, left, top, scale;

      &::after {
        transition-timing-function: ease-out;
        transition-duration: 150ms;
        transition-property: opacity, scale;
      }
    }
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover .media-slider__thumb {
      opacity: 1;
      scale: 1;
    }
  }

  &[data-dragging] .media-slider__thumb {
    opacity: 1;
    scale: 0.9;

    @media (prefers-reduced-motion: no-preference) {
      transition-property: opacity, outline-offset, scale;
    }

    &[data-orientation="horizontal"] {
      left: var(--media-slider-pointer);
    }
    &[data-orientation="vertical"] {
      top: calc(100% - var(--media-slider-pointer));
    }
  }

  /* Preview */
  .media-slider__preview {
    --media-max-size-factor: 36;
    --media-max-size: min(calc(var(--media-spacing) * var(--media-max-size-factor)), 100cqi);

    min-width: var(--media-max-size);
    height: calc(var(--media-spacing) * 1);

    @container media-root (width > 42rem) {
      --media-max-size-factor: 48;
    }

    .media-slider__thumbnail,
    .media-slider__value {
      position: absolute;
      left: 50%;
      max-width: var(--media-max-size);
      opacity: 0;
      filter: blur(8px);
      transform-origin: bottom;
      scale: 0.8;
      translate: -50% calc(var(--media-spacing) * 2);
      transition-timing-function: ease-out;
      transition-duration: 150ms;
    }

    .media-slider__thumbnail {
      --media-thumbnail-max-width: var(--media-max-size);
      --media-thumbnail-max-height: var(--media-max-size);
      bottom: calc(100% + (var(--media-spacing) * 9));
    }

    .media-slider__value {
      bottom: calc(100% + (var(--media-spacing) * 10.5));
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .media-slider__chapter-title {
      min-width: 0;
      max-width: var(--media-max-size);
      padding-inline: calc(var(--media-spacing) * 6);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &:empty {
        display: none;
      }
    }

    &::before {
      position: absolute;
      top: 50%;
      left: 50%;
      z-index: 1;
      width: calc(var(--media-spacing) * 1);
      height: calc(var(--media-spacing) * 1);
      pointer-events: none;
      content: "";
      background-color: currentColor;
      border-radius: 100%;
      box-shadow:
        0 0 0 1px var(--media-shadow-current-color, oklch(0 0 0 / 0.15)),
        0 1px 2px 0 oklch(0 0 0 / 0.35);
      opacity: 0;
      scale: 0.5;
      translate: -50% -50%;
      transition-timing-function: ease-out;
      transition-duration: 200ms;
      transition-property: opacity, scale;
    }
  }

  &:is([data-pointing], :has(:focus-visible))
    .media-slider__preview
    :is(.media-slider__value, .media-slider__thumbnail),
  .media-slider__preview[data-pointing]:not([data-dragging])::before {
    opacity: 1;
    filter: blur(0);
    scale: 1;
  }
}
